import { createApp } from 'vue';
import { clerkPlugin } from '@clerk/vue';
import App from './App.vue';
import router from './router';
import i18n from './i18n';
import './globals.css';

const app = createApp(App);

// Helper to decode Frontend API domain from Clerk Publishable Key (pk_test_... or pk_live_...)
function extractClerkFrontendApi(publishableKey: string): string | null {
  try {
    const parts = publishableKey.split('_');
    if (parts.length >= 3) {
      const b64 = parts[2].replace(/\$$/, '').replace(/-/g, '+').replace(/_/g, '/');
      const decoded = atob(b64);
      return decoded.replace(/\$$/, '');
    }
  } catch (err) {
    console.warn('[Clerk Auth] Notice extracting frontend API domain:', err);
  }
  return null;
}

// Initialize Clerk Authentication if publishable key is present
const clerkPublishableKey = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;
if (clerkPublishableKey) {
  try {
    const fapiDomain = extractClerkFrontendApi(clerkPublishableKey);
    const clerkScriptUrl = fapiDomain
      ? `https://${fapiDomain}/npm/@clerk/clerk-js@5/dist/clerk.browser.js`
      : `https://cdn.jsdelivr.net/npm/@clerk/clerk-js@5/dist/clerk.browser.js`;

    app.use(clerkPlugin, {
      publishableKey: clerkPublishableKey,
      clerkJSUrl: clerkScriptUrl,
    });
    console.info('[Clerk Auth] Initialized Clerk plugin with VITE_CLERK_PUBLISHABLE_KEY');

    // Also ensure Clerk JS script is loaded with UI components from Clerk FAPI
    if (typeof window !== 'undefined' && !(window as any).Clerk) {
      const script = document.createElement('script');
      script.setAttribute('data-clerk-publishable-key', clerkPublishableKey);
      script.crossOrigin = 'anonymous';
      script.async = true;
      script.src = clerkScriptUrl;
      script.onload = async () => {
        if ((window as any).Clerk) {
          try {
            await (window as any).Clerk.load();
            console.info('[Clerk Auth] Clerk Browser Client SDK loaded with UI components');
          } catch (loadErr) {
            console.warn('[Clerk Auth] Clerk.load notice:', loadErr);
          }
        }
      };
      document.head.appendChild(script);
    }
  } catch (err) {
    console.warn('[Clerk Auth] Initialization notice:', err);
  }
} else {
  console.info('[Clerk Auth] VITE_CLERK_PUBLISHABLE_KEY not set; using local persona simulation.');
}

// Register Vue Router & i18n
app.use(router);
app.use(i18n);

// Mount Application
app.mount('#app');
